/**
 * The start-up wizard's vocabulary: what the catalog offers, what the tenant
 * picked, and the pure rules between the two (KAN-1473).
 *
 * Moved verbatim out of ``pages/OnboardingStart.tsx`` so the first three steps
 * can be mounted outside the tenant admin — the platform landing's anonymous
 * pre-registration walks the same steps over a public catalog. Nothing here
 * talks to the API; where the catalog comes from is the page's business.
 */

import type { MaterialOptionPublic } from "@/api/types";
import type { JsonSchema } from "@/components/admin/ModuleConfigForm";
import { fillMissingDefaults } from "@/components/admin/processConfigSplit";

/**
 * The real onboarding rail's stops; the last one is „Doklady". Shared with
 * the sign-up-free tryout, which walks the first of them and says how many
 * there are in total (KAN-1944).
 */
export const ONBOARDING_RAIL = [
  "operations",
  "blocks",
  "delivery",
  "company",
  "price",
  "documents",
] as const;

export type Buyers = "b2b" | "b2c" | "both";

export type WizardMachine = {
  machine_key: string;
  /** ``base`` or ``variant`` — an addon is never a pickable machine. */
  kind?: "base" | "variant";
  /** The base a variant belongs under; ``null`` for a base itself. */
  parent_key?: string | null;
  name: string;
  vendor?: string | null;
  model?: string | null;
  width_m?: number | null;
  depth_m?: number | null;
  height_m?: number | null;
  /** Working space for this operation, mm per declared axis (KAN-1343). */
  build_volume_mm?: number[] | null;
  /** Already composed (base ⊕ variant) — never the stored difference. */
  config: Record<string, unknown>;
  /** Platform material codes this machine processes (KAN-1323). */
  material_codes?: string[];
  /** KAN-1945 — offered by the landing-page sample calculator (the platform
   *  admin ticks it); the wizard itself lists every machine. */
  in_demo?: boolean;
  /** The catalog's weekly schedule (KAN-1536) — only a prefill of
   *  „Kdy dílna pracuje?", never written unasked. */
  weekly_schedule?: Record<string, unknown>;
};

/** An addon the tenant can tick next to a base, and what it changes. */
type WizardAddon = {
  machine_key: string;
  name: string;
  description?: string | null;
  applies_to_keys: string[];
  /** ``{key: [without, with]}`` for this operation. */
  changes: Record<string, [unknown, unknown]>;
};

type WizardMaterial = {
  code: string;
  category_slug: string;
  display_name: string;
  /** Short customer note from the platform catalog (KAN-1326). */
  note?: string | null;
  /** Option values of the material, keyed by property key (KAN-1559). */
  option_sources?: Record<string, MaterialOptionPublic[]>;
  /** Numeric axis values, keyed by the operation's ``variant_axis.key``. */
  variant_axis_values?: Record<string, number[]>;
};

/** The operation's numeric variant axis — which form field the material's
 *  ``variant_axis_values`` fill (KAN-1559). */
export type WizardVariantAxis = { key: string; unit?: string | null };

/**
 * A platform machine offered under an own operation (KAN-1383) — identity
 * only: no config, no materials. ``operation_categories`` are the categories
 * of the catalog operations it runs.
 */
export type UnverifiedMachine = {
  machine_key: string;
  name: string;
  vendor?: string | null;
  model?: string | null;
  operation_categories: string[];
};

export type WizardOperation = {
  slug: string;
  display_name: string;
  /** What the operation is, for the customer (KAN-1326). */
  description?: string | null;
  operation_category: string;
  material_category: string;
  machine_config_schema: JsonSchema | null;
  machine_position_schema?: JsonSchema | null;
  machines: WizardMachine[];
  addons?: WizardAddon[];
  materials: WizardMaterial[];
  variant_axis?: WizardVariantAxis | null;
  /** KAN-1383 — only an own operation: platform machines nobody verified
   *  for it, offered with a warning. */
  unverified_machines?: UnverifiedMachine[];
};

type WizardCountry = {
  country_code: string;
  vat_rate_pct?: number | null;
  extra_days: number;
};

/** A country the tenant may do business FROM, with the platform country
 *  catalog's suggestions for it (KAN-1453). `null` = the catalog says
 *  nothing — never the same as zero. */
export type WizardHomeCountry = {
  code: string;
  name_i18n: Record<string, string>;
  alpha3: string | null;
  aliases: string[];
  dial_code: string | null;
  default_currency_code: string | null;
  catalog_vat_pct: string | null;
  postal_code_example: string | null;
};

/** „Navázat ``target`` za ``source``?" — learned from how other shops wired
 *  their Maps (KAN-2159). ``shop_count`` is the evidence the wizard shows. */
export type WizardChain = {
  source: string;
  target: string;
  shop_count: number;
};

export const chainKey = (chain: { source: string; target: string }): string =>
  `${chain.source}>${chain.target}`;

export type WizardCatalog = {
  operations: WizardOperation[];
  countries: WizardCountry[];
  home_countries?: WizardHomeCountry[];
  chains?: WizardChain[];
};

/** One catalog band of a carrier — what the seed writes unless retyped (KAN-1837). */
export type WizardCarrierRate = {
  country_code: string;
  max_weight_kg: number;
  price_minor: number;
  currency_code: string;
  extra_days: number;
};

export type WizardCarrier = {
  code: string;
  name: string;
  logo_url?: string | null;
  /** How the parcel reaches the customer (KAN-1326). */
  delivery_method?: string | null;
  extra_days: number;
  country_codes: string[];
  /** Absent on the landing's public catalog — only the admin wizard reviews prices. */
  rates?: WizardCarrierRate[];
};

/** A speed level of the platform template, as the seed copies it (KAN-1837). */
export type WizardDeliveryTier = {
  code: string;
  label: string;
  base_min_days: number;
  multiplier: number;
  flat_surcharge_minor: number;
  currency_code: string;
  /** KAN-2015 — ``business`` = pracovní dny výroby, ``calendar`` = kalendářní. */
  day_basis?: "business" | "calendar" | string;
};

/** What the tenant changed on a speed level — the three numbers the seed copies. */
export type WizardTierValues = {
  base_min_days: number;
  multiplier: number;
  flat_surcharge_minor: number;
};

/** „Založit vlastní" rather than a catalog machine. */
export const CUSTOM = "__custom__";

/** Jeden stroj, který tenant u operace přidal (KAN-1197). */
export type MachineEntry = {
  /** Stabilní klíč řádku — řádky se odebírají i ze středu seznamu. */
  id: number;
  /** Výrobce vybraného stroje; ``CUSTOM`` pro „Založit vlastní", ``""`` dokud nevybral. */
  vendor: string;
  /** A ``machine_key`` from the catalog, ``CUSTOM``, or ``""`` until a model is picked. */
  machineKey: string;
  /**
   * Doplňky zaškrtnuté na kartě (KAN-1151/1152). Co s nimi config udělá,
   * skládá server; tady je to jen seznam klíčů.
   */
  addonKeys: string[];
  /** „Můj stroj v katalogu není" (KAN-1323): výrobce, model a materiály. */
  customVendor: string;
  customModel: string;
  customMaterialCodes: string[];
  customConfig: Record<string, unknown>;
  /** KAN-1383 — the platform machine an own operation's row took its
   *  identity from; the row is still an own machine, with a warning. */
  unverifiedKey?: string;
  /** The catalog name of that machine — the seeded machine is named so. */
  customName?: string;
  /** „Kolik jich máte" (KAN-1319) — N kusů se stejným nastavením, 1–99. */
  quantity: number;
};

/** Hranice počítadla kusů; táž, jakou drží ``WizardMachineChoice.quantity``. */
export const MAX_QUANTITY = 99;

/** Co smí „Přidat vlastní operaci" nabídnout (KAN-1320) — z dat, ne z FE. */
export type CustomOperationOptions = {
  allowed: boolean;
  operation_categories: Array<{ slug: string; label: string }>;
  /** ``materials`` = what a custom operation of the category accepts. */
  material_categories: Array<{ slug: string; label: string; materials: WizardMaterial[] }>;
  /** KAN-1383 — the platform's machines, identity only. */
  machines?: UnverifiedMachine[];
};

/**
 * Vlastní operace pojmenovaná v průvodci. Založí ji až backend při
 * „Postavit obchod"; ``key`` žije jen v prohlížeči a páruje ji se stroji.
 * Bez „za co si účtujete" — rozhodnutí vlastníka 2026-09-14.
 */
export type CustomOperationDraft = {
  key: string;
  name: string;
  description: string;
  operationCategory: string;
  materialCategory: string;
};

const CUSTOM_OPERATION_PREFIX = "custom-operation:";
export const isCustomOperation = (slug: string) => slug.startsWith(CUSTOM_OPERATION_PREFIX);
let nextCustomOperationId = 0;
/** A fresh browser-only key for an own operation named in the wizard. */
export const nextCustomOperationKey = (): string =>
  `${CUSTOM_OPERATION_PREFIX}${nextCustomOperationId++}`;

/**
 * Vlastní operace v podobě katalogové — bez strojů z katalogu, bez vzorového
 * dílu. Materiály jsou platformní materiály zvolené kategorie, jinak by
 * „Z čeho vyrábíte?" nemělo co nabídnout.
 */
export function customWizardOperation(
  draft: CustomOperationDraft,
  options: CustomOperationOptions | null,
): WizardOperation {
  return {
    slug: draft.key,
    display_name: draft.name,
    description: draft.description || null,
    operation_category: draft.operationCategory,
    material_category: draft.materialCategory,
    machine_config_schema: null,
    machines: [],
    unverified_machines: options?.machines ?? [],
    materials:
      options?.material_categories.find((c) => c.slug === draft.materialCategory)
        ?.materials ?? [],
  };
}

export type OperationChoice = {
  /** V pořadí přidání — první se stane výchozím strojem uzlu. */
  machines: MachineEntry[];
  materialCodes: string[];
  /** KAN-2012 bod 6 — „Jaké tloušťky zpracujete?"; ``undefined`` = všechny. */
  axisRange?: { min: number; max: number } | null;
};

/** Výrobce řádku katalogu bez vyplněného ``vendor`` — i ten musí jít vybrat. */
export const NO_VENDOR = "__no_vendor__";

let nextMachineEntryId = 0;

export function vendorOf(card: MachineCard): string {
  return card.base.vendor?.trim() || NO_VENDOR;
}

/**
 * Nový řádek stroje. S ``machineKey`` je předvybraný (první řádek operace),
 * bez něj čeká na výrobce. Operace bez katalogového stroje nemá co nabídnout
 * kromě „Založit vlastní", takže řádek rovnou je.
 */
export function newMachineEntry(op: WizardOperation, machineKey = ""): MachineEntry {
  const card = cardOf(machineCards(op.machines), machineKey);
  const customOnly = op.machines.length === 0;
  return {
    id: nextMachineEntryId++,
    vendor: customOnly ? CUSTOM : card ? vendorOf(card) : "",
    machineKey: customOnly ? CUSTOM : machineKey,
    addonKeys: [],
    customVendor: "",
    customModel: "",
    customMaterialCodes: [],
    // Defaulty schématu jako u dialogu stroje (KAN-1158): nedotčené
    // pole by jinak vzniklo bez čísla a brána by stroj s limitem na
    // tom klíči odmítala. Katalogový stroj je nepotřebuje — dědí.
    customConfig: fillMissingDefaults(op.machine_config_schema, {}),
    quantity: 1,
  };
}

/**
 * Karta stroje: základ a varianty, mezi kterými se přepíná.
 *
 * Vzniká z DAT — `parent_key` od dědičnosti katalogu — ne z heuristiky
 * „shodný vendor + model", která by slila dva nezávislé stroje téhož
 * výrobce do jedné karty (KAN-1146, doplnění po KAN-1151).
 */
type MachineCard = {
  base: WizardMachine;
  choices: WizardMachine[];
};

export function machineCards(machines: WizardMachine[]): MachineCard[] {
  const bases = machines.filter((m) => (m.kind ?? "base") === "base");
  return bases.map((base) => ({
    base,
    choices: [
      base,
      ...machines.filter((m) => m.parent_key === base.machine_key),
    ],
  }));
}

/** Karta, na které leží vybraný stroj — základ nebo jeho varianta. */
export function cardOf(cards: MachineCard[], machineKey: string): MachineCard | undefined {
  return cards.find((card) =>
    card.choices.some((choice) => choice.machine_key === machineKey),
  );
}

/** „Výrobce Model" ručního stroje — týž název, jaký mu dá seed. */
export function customMachineLabel(entry: MachineEntry): string {
  const typed = [entry.customVendor, entry.customModel]
    .map((part) => part.trim())
    .filter(Boolean)
    .join(" ");
  // KAN-1383 — a platform machine's row is seeded under its catalog name
  // until the tenant retypes vendor or model.
  return entry.customName?.trim() || typed;
}

/**
 * Materiály, které jeden řádek stroje zpracuje (KAN-1323): u katalogového
 * stroje odvozené serverem (``material_codes``), u ručního zaškrtnuté čipy.
 */
export function machineMaterialCodes(op: WizardOperation, entry: MachineEntry): string[] {
  // KAN-1383 — an unverified platform machine processes what the own
  // operation does, not what the catalog says about it.
  if (entry.unverifiedKey) return op.materials.map((material) => material.code);
  if (entry.machineKey === CUSTOM) return entry.customMaterialCodes;
  return (
    op.machines.find((machine) => machine.machine_key === entry.machineKey)
      ?.material_codes ?? []
  );
}

/** Materiály operace, které zvládne aspoň jeden z vybraných strojů. */
export function offeredMaterialCodes(
  op: WizardOperation,
  machines: MachineEntry[],
): Set<string> {
  const codes = new Set(machines.flatMap((entry) => machineMaterialCodes(op, entry)));
  return new Set(op.materials.map((m) => m.code).filter((code) => codes.has(code)));
}

/** Odškrtne materiály, které po změně strojů už žádný stroj neumí. */
export function withOfferedMaterials(
  op: WizardOperation,
  next: OperationChoice,
): OperationChoice {
  const offered = offeredMaterialCodes(op, next.machines);
  return {
    ...next,
    materialCodes: next.materialCodes.filter((code) => offered.has(code)),
  };
}

/** A country's name in the reader's language; the bare code when unknown. */
export function regionName(lang: string, code: string): string {
  try {
    return new Intl.DisplayNames([lang], { type: "region" }).of(code) ?? code;
  } catch {
    return code;
  }
}

/**
 * KAN-2019 — čím se varianty jedné karty liší, aby to bylo vidět u KAŽDÉ.
 *
 * „TRUMPF TruLaser 1030 fiber" vs „… — 6 kW": název varianty rozdíl nese,
 * základ ne, a nováček neví, čím se liší. Ze sestavených configů (základ ⊕
 * varianta) se vezmou skalární klíče, jejichž hodnota se mezi volbami karty
 * liší, a u každé volby se vypíšou jejím titulkem a jednotkou ze schématu
 * operace. Žádný klíč („výkon") se tu nejmenuje — co se liší, říkají data.
 */
export function variantSpecs(
  choices: WizardMachine[],
  schema: JsonSchema | null | undefined,
  formatNumber: (value: number) => string,
  /** Co říct u volby, které hodnota chybí („neuvedeno"). */
  missing: string,
): Record<string, string> {
  const specs: Record<string, string> = {};
  if (choices.length < 2) return specs;
  const keys = new Set(choices.flatMap((m) => Object.keys(m.config ?? {})));
  const differing = [...keys].filter((key) => {
    const values = choices.map((m) => m.config?.[key]);
    if (values.some((v) => v !== undefined && v !== null && typeof v === "object")) {
      return false;
    }
    return new Set(values.map((v) => JSON.stringify(v ?? null))).size > 1;
  });
  for (const machine of choices) {
    specs[machine.machine_key] = differing
      .map((key) => {
        const value = machine.config?.[key];
        const prop = schema?.properties?.[key];
        if (value === undefined || value === null || value === "") {
          return prop?.title ? `${prop.title} ${missing}` : "";
        }
        const shown =
          typeof value === "number"
            ? formatNumber(value)
            : typeof value === "boolean"
              ? value
                ? "✓"
                : "✗"
              : String(value);
        const unit = prop?.unit ? ` ${prop.unit}` : "";
        return prop?.title ? `${prop.title} ${shown}${unit}` : `${shown}${unit}`;
      })
      .filter(Boolean)
      .join(" · ");
  }
  return specs;
}

/**
 * KAN-2015 — nejtěžší zásilka, kterou vybraní dopravci vůbec uvezou.
 *
 * Pokladna nabídne jen dopravce s pásmem, do kterého se váha košíku vejde
 * (``checkout.quote_carriers_for_cart`` / ``cart_shipping_fits_nowhere``,
 * KAN-1825); těžší košík dopravu nedostane a zákazník pošle poptávku, nebo
 * si objednávku vyzvedne. ``null`` = žádný vybraný dopravce s pásmy.
 */
export function heaviestBandKg(
  carriers: WizardCarrier[],
  carrierCodes: string[],
): number | null {
  const weights = carriers
    .filter((c) => carrierCodes.includes(c.code))
    .flatMap((c) => (c.rates ?? []).map((band) => band.max_weight_kg));
  return weights.length > 0 ? Math.max(...weights) : null;
}

/**
 * KAN-2015 — nejrychlejší doprava, jakou pokladna slíbí do ``countryCode``:
 * dny dopravce + dny země + dny pásma, minimum přes vybrané dopravce. Týž
 * součet jako ``checkout.fastest_transit_days`` (z něj web obchodu píše
 * „Express od N dnů"), takže průvodce, pokladna i web říkají totéž číslo.
 * ``null`` = žádný vybraný dopravce do té země nevozí.
 */
export function fastestTransitDays(
  carriers: WizardCarrier[],
  carrierCodes: string[],
  countryCode: string | undefined,
  countryExtraDays: number,
): number | null {
  if (!countryCode) return null;
  const totals = carriers
    .filter((c) => carrierCodes.includes(c.code))
    .flatMap((c) => {
      const bands = (c.rates ?? []).filter((b) => b.country_code === countryCode);
      if (bands.length === 0) return [];
      return [
        (c.extra_days ?? 0) +
          countryExtraDays +
          Math.min(...bands.map((b) => b.extra_days ?? 0)),
      ];
    });
  return totals.length > 0 ? Math.min(...totals) : null;
}

/**
 * KAN-2012 bod 6 — hodnoty číselné osy variant (tloušťky), které nabízí
 * aspoň jeden VYBRANÝ materiál operace; seřazené, bez duplicit. Prázdné =
 * operace osu nemá, nebo ji vybrané materiály nenesou (krok se pak neptá).
 */
export function axisValuesOf(op: WizardOperation, materialCodes: string[]): number[] {
  const key = op.variant_axis?.key;
  if (!key) return [];
  const values = new Set<number>();
  for (const m of op.materials) {
    if (!materialCodes.includes(m.code)) continue;
    for (const v of m.variant_axis_values?.[key] ?? []) values.add(v);
  }
  return [...values].sort((a, b) => a - b);
}

/** Rozsah, který se pošle seedu: jen když je užší než vše nabízené. */
export function effectiveAxisRange(
  values: number[],
  range: { min: number; max: number } | null | undefined,
): { min: number; max: number } | null {
  if (!range || values.length === 0) return null;
  if (range.min <= values[0] && range.max >= values[values.length - 1]) return null;
  return range;
}
